import React, { useState, useEffect } from 'react'
import AgingReportInv from './AgingReport/AgingReportInv';
import AgingReport from './AgingReport/AgingReport';
import { DownloadIcon } from 'lucide-react/dist/cjs/lucide-react';
import { ColorRing } from 'react-loader-spinner';
import { useSelector } from 'react-redux';
import { MdKeyboardArrowRight, MdKeyboardArrowDown } from "react-icons/md";
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import ColorClaritySelector from './colorClaritySelector';
import { selectShapeCaratState } from '../redux/slices/shapeCaratSlice';
import IntransitReport from './AgingReport/IntransitReport';
import { FiExternalLink } from 'react-icons/fi';

const TableStockAndSales = ({
  key,
  isMFG,
  isAdmin,
  handleSearchClick,
  drilldownRef,
  isCsr,
  inventoryFilter,
  salesFilter,
  // Add new props for dropdown data and handlers
  shapeOpts,
  colorOpts = [],
  clarityOpts = [],
  caratRangeOpts = [],
  onFilterChange
}) => {
  // Minimal shape label -> code mapping to update filters from dropdown
  const SHAPE_LABELS = React.useMemo(() => ({
    round: "0000round",
    princess: "0001princess",
    pear: "0002pear",
    oval: "0003oval",
    emerald: "0004emerald",
    marquise: "0005marquise",
    heart: "0006heart",
    cushion: "0007cushion_brilliant",
    cushion_brilliant: "0007cushion_brilliant",
    radiant: "0009radiant",
    "square radiant": "0010square_radiant",
    square_radiant: "0010square_radiant",
    asscher: "0011asscher",
    other: "0064other_shape",
    other_shape: "0064other_shape",
  }), []);

  const shapeToCode = React.useCallback((label) => {
    const k = String(label || "").trim().toLowerCase().replace(/\s+/g, "_");
    return SHAPE_LABELS[k] || null;
  }, [SHAPE_LABELS]);

  const {
    selectedCell
  } = useSelector(selectShapeCaratState);
  // Local state for selected values
  const [selectedShape, setSelectedShape] = useState("");
  const [selectedCaratRange, setSelectedCaratRange] = useState("");
  const [selectedColors, setSelectedColors] = useState([]);
  const [selectedClarities, setSelectedClarities] = useState([]);
  
  // Helper function to decode shape from encoded value
  const decodeShape = (encodedShape) => {
    if (!encodedShape) return "";
    // If it's already human-readable (no numeric prefix), return as-is
    if (!/^\d+/.test(encodedShape)) {
      const plain = String(encodedShape).replace(/_/g, " ");
      const n = plain.trim().toLowerCase();
      if (n === "other shape" || n === "other") return "Other";
      return plain;
    }
    // Remove the numeric prefix and convert to readable format
    const shape = encodedShape.replace(/^\d+/, "").replace(/_/g, " ");
    const n = shape.trim().toLowerCase();
    if (n === "other shape" || n === "other") return "Other";
    return shape; // preserve original casing of options (no forced capitalization)
  };

  const normalize = React.useCallback((v) => String(v ?? "").trim().toLowerCase().replace(/\s+/g, " "), []);
  const normalizeShape = React.useCallback((v) => normalize(v).replace(/_/g, " "), [normalize]);
  const normalizeCarat = React.useCallback((v) => normalize(v).replace(/\s*-\s*/g, " - "), [normalize]);

  const findMatchingOption = React.useCallback((options, target, kind = 'shape') => {
    const normTarget = kind === 'carat' ? normalizeCarat(target) : normalizeShape(target);
    for (const opt of options || []) {
      const normOpt = kind === 'carat' ? normalizeCarat(opt) : normalizeShape(opt);
      if (normOpt === normTarget) return opt;
    }
    return null;
  }, [normalizeCarat, normalizeShape]);
  
  // (no-op) carat range decode helper removed as unused
  
  // Set initial values when filters change
  useEffect(() => {
    if (inventoryFilter?.filterHash?.item_filter?.discrete) {
      const discrete = inventoryFilter.filterHash.item_filter.discrete;
      
      // Decode and set shape
      if (discrete["diamond#shape"]) {
        const rawShape = Array.isArray(discrete["diamond#shape"]) ? discrete["diamond#shape"][0] : discrete["diamond#shape"]; 
        const decoded = decodeShape(rawShape);
        const match = findMatchingOption(shapeOpts, decoded, 'shape') || findMatchingOption(shapeOpts, rawShape, 'shape');
        setSelectedShape(match || decoded || rawShape);
      }
      
      // Decode and set carat range
      if (discrete["gem#carats"]) {
        const rawCarats = Array.isArray(discrete["gem#carats"]) ? discrete["gem#carats"][0] : discrete["gem#carats"]; 
        let display = "";
        if (typeof rawCarats === 'string') {
          display = rawCarats.includes('-') ? rawCarats.replace(/\s*-\s*/g, ' - ') : "";
        } else if (rawCarats && typeof rawCarats === 'object') {
          const from = rawCarats.from;
          const to = rawCarats.to;
          if (from != null && to != null) display = `${from} - ${to}`;
        }
        const match = findMatchingOption(caratRangeOpts, display, 'carat');
        setSelectedCaratRange(match || display);
      }
      
      // Set colors and clarities (these are already in the right format)
      if (discrete["diamond#color"]) {
        setSelectedColors(discrete["diamond#color"]);
      }
      if (discrete["diamond#clarity"]) {
        setSelectedClarities(discrete["diamond#clarity"]);
      }
    }
  }, [inventoryFilter, shapeOpts, caratRangeOpts, findMatchingOption]);
  
  // Set default values if no filters are set
  useEffect(() => {
    if (shapeOpts && shapeOpts.length > 0 && !selectedShape) {
      const fallback = decodeShape(selectedCell?.shape) || shapeOpts[0];
      const match = findMatchingOption(shapeOpts, fallback, 'shape');
      setSelectedShape(match || fallback);
    }
    if (caratRangeOpts && caratRangeOpts.length > 0 && !selectedCaratRange) {
      const fromRedux = typeof selectedCell?.caratRange === 'string' ? selectedCell.caratRange.replace(/\s*-\s*/g, ' - ') : '';
      const fallback = fromRedux || caratRangeOpts[0];
      const match = findMatchingOption(caratRangeOpts, fallback, 'carat');
      setSelectedCaratRange(match || fallback);
    }
  }, [shapeOpts, caratRangeOpts, selectedShape, selectedCaratRange, selectedCell, findMatchingOption]);
  
  // Generate unique keys for aging reports to force re-render when filters change
  const [filterKey, setFilterKey] = useState(0);
  
  const updateSearchParam = (key, values) => {
    const url = new URL(window.location);
    if (values?.length) {
      url.searchParams.set(key, values.join(','));
    } else {
      url.searchParams.delete(key); // Remove param if empty
    }
    window.history.replaceState({}, "", url);
  };

  // Simple handlers that update local state and trigger filter refresh
  const handleShapeChange = (newShape) => {
    setSelectedShape(newShape);
    setFilterKey(prev => prev + 1);
    
    // Update URL param
    const code = shapeToCode(newShape) || newShape;
    updateSearchParam("sh", [code]);

    // Update filters (mutate passed-in filters similarly to existing code)
    if (inventoryFilter?.filterHash?.item_filter?.discrete) {
      inventoryFilter.filterHash.item_filter.discrete["diamond#shape"] = [code];
    }
    if (salesFilter?.filterHash?.item_filter?.discrete) {
      salesFilter.filterHash.item_filter.discrete["diamond#shape"] = [code];
    }
    
    // Notify parent if callback exists
    if (onFilterChange) {
      onFilterChange({
        type: 'shape',
        value: newShape
      });
    }
  };

  const handleCaratChange = (newCaratRange) => {
    setSelectedCaratRange(newCaratRange);
    setFilterKey(prev => prev + 1);
    
    // Prepare carat object and URL param (no spaces around hyphen)
    let from = undefined, to = undefined;
    if (typeof newCaratRange === 'string' && newCaratRange.includes('-')) {
      const parts = newCaratRange.split('-').map(t => t.trim());
      const f = parseFloat(parts[0]);
      const t = parseFloat(parts[1]);
      if (Number.isFinite(f)) from = parts[0];
      if (Number.isFinite(t)) to = parts[1];
    }
    const caratObj = (from != null && to != null) ? { from, to } : null;
    const crParam = caratObj ? `${caratObj.from}-${caratObj.to}` : (typeof newCaratRange === 'string' ? newCaratRange.replace(/\s+/g, '') : '');
    // Update URL param with normalized value
    updateSearchParam("cr", [crParam]);

    if (caratObj) {
      if (inventoryFilter?.filterHash?.item_filter?.discrete) {
        inventoryFilter.filterHash.item_filter.discrete["gem#carats"] = [caratObj];
      }
      if (salesFilter?.filterHash?.item_filter?.discrete) {
        salesFilter.filterHash.item_filter.discrete["gem#carats"] = [caratObj];
      }
    }
    
    // Notify parent if callback exists
    if (onFilterChange) {
      onFilterChange({
        type: 'carat',
        value: newCaratRange
      });
    }
  };

  const handleColorChange = (newColors) => {
    setSelectedColors(newColors);
    setFilterKey(prev => prev + 1);
    
    // Update URL param
    updateSearchParam("col", newColors);

    // Update filters directly with codes array
    if (inventoryFilter?.filterHash?.item_filter?.discrete) {
      inventoryFilter.filterHash.item_filter.discrete["diamond#color"] = newColors?.length ? [...newColors] : [];
    }
    if (salesFilter?.filterHash?.item_filter?.discrete) {
      salesFilter.filterHash.item_filter.discrete["diamond#color"] = newColors?.length ? [...newColors] : [];
    }
    
    // Notify parent if callback exists
    if (onFilterChange) {
      onFilterChange({
        type: 'color',
        value: newColors
      });
    }
  };

  const handleClarityChange = (newClarities) => {
    setSelectedClarities(newClarities);
    setFilterKey(prev => prev + 1);
    
    // Update URL param
    updateSearchParam("clr", newClarities);

    // Update filters directly with codes array
    if (inventoryFilter?.filterHash?.item_filter?.discrete) {
      inventoryFilter.filterHash.item_filter.discrete["diamond#clarity"] = newClarities?.length ? [...newClarities] : [];
    }
    if (salesFilter?.filterHash?.item_filter?.discrete) {
      salesFilter.filterHash.item_filter.discrete["diamond#clarity"] = newClarities?.length ? [...newClarities] : [];
    }
    
    // Notify parent if callback exists
    if (onFilterChange) {
      onFilterChange({
        type: 'clarity',
        value: newClarities
      });
    }
  };

  const [showInventoryReport, setShowInventoryReport] = useState(true);
  const [showIntransitReport, setShowIntransitReport] = useState(true);
  const [showSalesReport, setShowSalesReport] = useState(true);
  const shapes_others = ["0012square_emerald", "0013tapered_baguette", "0014baguette", "0015europeancut", "0016old_miner", "0017star", "0018rose", "0019half_moon", "0020square", "0023trapezoid", "0025briolette", "0026pentagonal", "0027hexagonal", "0028octagonal", "0029triangle", "0030trilliant", "0032shield", "0033lozenge", "0034kite", "0035round_modified_brilliant", "0036rectangular", "0037pear_rose_cut", "0038heart_rose_cut", "0045trapezoid_step_cut", "0049shield_step_cut", "0051oval_mixed_cut", "0052kite_mixed_cut", "0054round_cornered_rectangular_modified_brilliant", "0055pear_modified", "0056oval_modified", "0057heart_modified", "0058triangular_modified", "0059marquise_modified", "0060shield_modified", "0061hexagonal_modified", "0064other_shape"]
  const summary  = useSelector((state => state.summary));

    if (inventoryFilter.filterHash.item_filter.discrete["diamond#shape"]?.includes("0007cushion_brilliant")) {
      inventoryFilter.filterHash.item_filter.discrete["diamond#shape"].push("0008cushion_modified");
    }

    if (inventoryFilter.filterHash.item_filter.discrete["diamond#shape"]?.includes("0064other_shape")) {
      inventoryFilter.filterHash.item_filter.discrete["diamond#shape"].push(...shapes_others);
    }

    if (salesFilter.filterHash.item_filter.discrete["diamond#shape"]?.includes("0007cushion_brilliant")) {
      salesFilter.filterHash.item_filter.discrete["diamond#shape"].push("0008cushion_modified");
    }

    if (salesFilter.filterHash.item_filter.discrete["diamond#shape"]?.includes("0064other_shape")) {
      salesFilter.filterHash.item_filter.discrete["diamond#shape"].push(...shapes_others);
    }
  
    const [downloadSale, setDownloadSale] = useState(false);
    

  return (
    <div className="drilldown">
          <div
            style={{
              paddingTop: "48px",
              paddingBottom: "40px",
              paddingLeft: "35px",
              paddingRight: "35px",
            }}
          >
            <div className="pagetitle text-center flex-col justify-center items-center" ref={drilldownRef}>
            <div className="flex justify-center items-center gap-4">
              <h1 className=" mb-7">
                STOCK AND SALES
              </h1>
              
              </div>  
              
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                {/* Shape Dropdown */}
                <FormControl variant="outlined" style={{ minWidth: 150 }} size="small">
                  <InputLabel>Shape</InputLabel>
                  <Select
                    value={selectedShape || decodeShape(selectedCell.shape)}
                    label="Shape"
                    onChange={(e) => handleShapeChange(e.target.value)}
                  >
                    {shapeOpts && shapeOpts.length > 0 ? shapeOpts.map(shape => (
                      <MenuItem key={shape} value={shape}>
                        {shape}
                      </MenuItem>
                    )) : (
                      <MenuItem value="" disabled>No shapes available</MenuItem>
                    )}
                  </Select>
                </FormControl>

                {/* Carat Range Dropdown */}
                <FormControl variant="outlined" style={{ minWidth: 150 }} size="small">
                  <InputLabel>Carat Range</InputLabel>
                  <Select
                    value={selectedCaratRange}
                    label="Carat Range"
                    onChange={(e) => handleCaratChange(e.target.value)}
                  >
                                         {caratRangeOpts && caratRangeOpts.length > 0 ? caratRangeOpts.map(range => (
                       <MenuItem key={range} value={range}>
                         {range.replace(/\s*-\s*/g, '-')}
                       </MenuItem>
                     )) : (
                       <MenuItem value="" disabled>No carat ranges available</MenuItem>
                     )}
                  </Select>
                </FormControl>

                {/* Color and Clarity Selector */}
                <ColorClaritySelector 
                  onSelectionChange={() => {}} // Not needed for this simple implementation
                  onColorChange={handleColorChange}
                  onClarityChange={handleClarityChange}
                  selectedCell={null}
                  setSalesFilterKey={() => {}}
                  setInventoryFilterKey={() => {}}
                  selectedGroupedCell={null}
                  initialColor={selectedColors}
                  initialClarity={selectedClarities}
                />
              </div>
        </div>
       <br />
          <div className="flex justify-between items-center py-1 rounded-md mb-4">
            <div className='flex gap-2 justify-between items-center'>
              <div className="flex justify-center items-center mb-2 hover:cursor-pointer  rounded-md">
                <div
                  className="text-black text-md underline"
                  onClick={() => setShowInventoryReport(prev => !prev)}
                >
                  {showInventoryReport ? <MdKeyboardArrowDown className="h-5 w-5" /> : <MdKeyboardArrowRight className="h-5 w-5"/>}
                </div>
              </div>
              <h4 className=" text-inherit text-2xl font-medium px-2">Inventory</h4>
               <div className="flex pb-1 justify-between items-center flex-wrap text-[0.75rem]">
                <div className="px-3 py-1  rounded-md  text-black ">
                  <span className="font-medium">COUNT:</span> {summary.selectedSummary?.inventory}
                </div>
                <div className="px-3 py-1  rounded-md text-black ">
                  <span className="font-medium">VALUE:</span> {summary.selectedSummary?.inv_value}
                </div>
                <div className="px-3 py-1  rounded-md text-black ">
                  <span className="font-medium">AVG AGING:</span> {summary.selectedSummary?.inv_aging}
                </div>
              </div>
            </div>
            {console.log("salesFilter.filterHash", salesFilter.filterHash)}
              <button 
                  className="px-4 py-2 text-blue-500 flex gap-2 items-center bg-white rounded-md border hover:bg-blue-600 transition-colors duration-200 text-sm font-medium"
                  onClick={() => handleSearchClick( (salesFilter.filterHash))}
                >
                Pricing Co-Pilot  <FiExternalLink />
              </button>
          </div>   
            <AgingReportInv
              key={`inventory-table-${key}-${filterKey}`}
              id={"BI-Table_Values"}
              isCsr={isCsr}
              filterHash={inventoryFilter.filterHash}
              rowFilter={salesFilter.rowFilter}
              showInventoryReport={showInventoryReport}
            />
          
          
          <div className="flex justify-between items-center py-1">
            <div className='flex gap-1 justify-between items-center'>
              <div className="flex justify-center items-center mb-2 mr-2 hover:cursor-pointer  rounded-md">
                <div
                  className="text-black text-md underline"
                  onClick={() => setShowSalesReport(prev => !prev)}
                >
                  {showSalesReport ? <MdKeyboardArrowDown className="h-5 w-5" /> : <MdKeyboardArrowRight className="h-5 w-5"/>}
                </div>
              </div>
              <h4 className=" text-inherit text-2xl font-medium px-1">Sales</h4>
            
              <div className=" pl-[3.25rem] flex pb-1 justify-between items-center flex-wrap text-[0.75rem]">
                <div className="px-3 py-1  rounded-md  text-black ">
                  <span className="font-medium">COUNT:</span> {summary.selectedSaleSummary?.sales}
                </div>
                <div className="px-3 py-1  rounded-md text-black ">
                  <span className="font-medium">VALUE:</span> {summary.selectedSaleSummary?.sold_value}
                </div>
                <div className="px-3 py-1  rounded-md text-black ">
                  <span className="font-medium">AVG AGING:</span> {summary.selectedSaleSummary?.sold_aging}
                </div>
              </div>
            </div>
              <button
                className="border-[1px] rounded-md bg-transparent font-medium text-[0.75rem] border-[#1E90FF] h-8 text-[#1E90FF] px-2 gap-[0.200rem] flex justify-center items-center disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={() => setDownloadSale(true)}
                disabled={downloadSale} // Disable button when downloadSale is true
              >
                {downloadSale ? (
                  <>
                    <ColorRing
                      visible={true}
                      height="30"
                      width="30"
                      ariaLabel="blocks-loading"
                      wrapperStyle={{}}
                      wrapperClass="blocks-wrapper"
                      colors={["#00319f", "#2e56b1", "#5c7bc2", "#8aa1d3", "#b2c1e2"]}
                    />
                    Downloading
                  </>
                ) : (
                  <>
                    <DownloadIcon className="h-4 w-4" /> Download
                  </>
                )}
              </button>
            </div>
          
            <AgingReport
              key={`sales-salesFilterKey-${key}-${filterKey}`}
              rmToggle={true}
              id={"stock-performance"}
              isAdmin={isAdmin}
              isCsr={isCsr}
              filterHash={salesFilter.filterHash}
              rowFilter={salesFilter.rowFilter}
              downloadSale={downloadSale}
              setDownloadSale={setDownloadSale}
              showSalesReport={showSalesReport}
            />
            {isMFG && (
                <>
                  <div className="flex justify-between items-center py-1 rounded-md my-4">
                  <div className='flex gap-2 justify-between items-center'>
                    <div className="flex justify-center items-center mb-2 hover:cursor-pointer  rounded-md">
                      <div
                        className="text-black text-md underline"
                        onClick={() => setShowIntransitReport(prev => !prev)}
                      >
                        {showIntransitReport ? <MdKeyboardArrowDown className="h-5 w-5" /> : <MdKeyboardArrowRight className="h-5 w-5"/>}
                      </div>
                    </div>
                    <h4 className=" text-inherit text-2xl font-medium px-2">Intransit</h4>
                    <div className="flex pb-1 justify-between items-center flex-wrap text-[0.75rem]">
                      <div className="px-3 py-1  rounded-md  text-black ">
                        <span className="font-medium">COUNT:</span> {summary.selectedIntransitSummary?.intransit}
                      </div>
                    </div>
                  </div>
                </div>   
                  <IntransitReport
                    key={`inventorymfg-table-${key}-${filterKey}`}
                    id={"BI-Table_Values"}
                    isCsr={isCsr}
                    filterHash={inventoryFilter.filterHash}
                    rowFilter={salesFilter.rowFilter}
                    showInventoryReport={showIntransitReport}
                  />
                </>
              )}  
          </div>
        </div>
  )
}

export default TableStockAndSales;